import React from 'react';

import { Box, ClassNameMap, debounce, GlobalStyles } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { Loader } from '../../Loader';
import { UpliftThemeProvider } from '../../theme';
import { StickyStackProvider } from '../StickyStack';

export const fullScreenLayoutBackgrounds: Record<string, string> = {
  grey: '#FAFAFA',
  white: '#FFFFFF',
  radiantPurple: 'linear-gradient(50deg, #F7F2FF 30.17%, #FFFFFF 83.75%)',
};


type fullScreenLayoutStylesProps = {
  background: keyof typeof fullScreenLayoutBackgrounds;
  classes?: ClassNameMap<'root'>;
};


const useFullScreenLayoutStyles = makeStyles<fullScreenLayoutStylesProps>()((_theme, { background }) => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: window.innerHeight,
    backgroundColor: fullScreenLayoutBackgrounds[background],
  },
}));


export type FullScreenLayoutProps = {
  background?: keyof typeof fullScreenLayoutBackgrounds;
  classes?: ClassNameMap<'root'>;
  loading?: boolean;
  navigation?: React.ReactElement;
};


const useWindowHeight = () => {
  const [ height, setHeight ] = React.useState(window.innerHeight);

  React.useEffect(() => {
    const handleResize = debounce(() => {
      setHeight(window.innerHeight);
    }, 150);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return height;
};


export const FullScreenLayout: React.FC<FullScreenLayoutProps> = ({
  classes: _classes,
  background = 'grey',
  navigation = null,
  loading,
  children,
}) => {
  const windowHeight = useWindowHeight();
  const { classes } = useFullScreenLayoutStyles({
    background
  }, {
    props: {
      classes: _classes
    }
  });

  const loader = <Loader color="primary" flex="1" height="100%" />;

  return (
    <UpliftThemeProvider>
      <GlobalStyles
        styles={{
          'html, body': {
            height: 'auto',
            minHeight: `${windowHeight ?? window.innerHeight}px`,
          }
        }}
      />
      <StickyStackProvider>
        <Box className={classes.root}>
          {navigation}
          <React.Suspense fallback={loader}>
            {loading ? loader : children}
          </React.Suspense>
        </Box>
      </StickyStackProvider>
    </UpliftThemeProvider>
  );
};
